import mapboxgl from "mapbox-gl";
import { CheckableLayer } from "../../types/Layer";
import { TRANSMISSION_LINE_COLORS } from "../../component/constants/transmissionInsightsConstants";

export const generateVoltageFilterExpression = (
    visibleFilters: CheckableLayer[],
    filterProperty?: string
): mapboxgl.ExpressionSpecification => {
    if (visibleFilters.length === 0) {
        return [
            "==",
            ["get", filterProperty],
            "none",
        ] as mapboxgl.ExpressionSpecification;
    }

    const filterConditions: mapboxgl.ExpressionSpecification = ["any"];

    visibleFilters.forEach((filter) => {
        // Since buses and transmission lines are initialized with
        // an id like (bus | transmission)-<100kv, buses and lines are differentiated
        // by splitting off whatever was before the first '-' and keeping the rest
        switch (filter.id.split("-").slice(1).join("-")) {
            case "<100kv":
                filterConditions.push([
                    "all",
                    [">=", ["get", filterProperty], 0],
                    ["<", ["get", filterProperty], 100],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "100kv-200kv":
                filterConditions.push([
                    "all",
                    [">=", ["get", filterProperty], 100],
                    ["<", ["get", filterProperty], 200],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "200kv-300kv":
                filterConditions.push([
                    "all",
                    [">=", ["get", filterProperty], 200],
                    ["<", ["get", filterProperty], 300],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "300kv-500kv":
                filterConditions.push([
                    "all",
                    [">=", ["get", filterProperty], 300],
                    ["<", ["get", filterProperty], 500],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "500kv":
                filterConditions.push([
                    "==",
                    ["get", filterProperty],
                    500,
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "765kv":
                filterConditions.push([
                    "==",
                    ["get", filterProperty],
                    765,
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "dc":
                filterConditions.push([
                    "==",
                    ["get", "TYPE"],
                    "DC",
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "others":
                filterConditions.push([
                    "==",
                    ["get", "TYPE"],
                    "Others",
                ] as mapboxgl.ExpressionSpecification);
                break;
        }
    });

    return filterConditions;
};

export const generateLoadFilterExpression = (
    visibleFilters: CheckableLayer[]
): mapboxgl.ExpressionSpecification => {
    if (visibleFilters.length === 0) {
        return [
            "==",
            ["get", "PL"],
            "none",
        ] as mapboxgl.ExpressionSpecification;
    }

    const filterConditions: mapboxgl.ExpressionSpecification = ["any"];

    visibleFilters.forEach((filter) => {
        switch (filter.id) {
            case "<10mv":
                filterConditions.push([
                    "all",
                    [">=", ["get", "PL"], 0],
                    ["<", ["get", "PL"], 10],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "10mv-50mv":
                filterConditions.push([
                    "all",
                    [">=", ["get", "PL"], 10],
                    ["<", ["get", "PL"], 50],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "50mv-100mv":
                filterConditions.push([
                    "all",
                    [">=", ["get", "PL"], 50],
                    ["<", ["get", "PL"], 100],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "100mv-200mv":
                filterConditions.push([
                    "all",
                    [">=", ["get", "PL"], 100],
                    ["<", ["get", "PL"], 200],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case "200mv-300mv":
                filterConditions.push([
                    "all",
                    [">=", ["get", "PL"], 200],
                    ["<", ["get", "PL"], 300],
                ] as mapboxgl.ExpressionSpecification);
                break;
            case ">300mv":
                filterConditions.push([
                    ">=",
                    ["get", "PL"],
                    300,
                ] as mapboxgl.ExpressionSpecification);
                break;
        }
    });

    return filterConditions;
};

export const generateGeneratorFilterExpression = (
    visibleFilters: CheckableLayer[],
    filterProperty?: string
): mapboxgl.ExpressionSpecification => {
    if (visibleFilters.length === 0) {
        return [
            "==",
            ["get", filterProperty],
            "none",
        ] as mapboxgl.ExpressionSpecification;
    }

    const filterConditions: mapboxgl.ExpressionSpecification = ["any"];

    visibleFilters.forEach((filter) => {
        // Use 'like' operator to check if the Technology string contains the filter value
        filterConditions.push([
            "in",
            filter.id.split("-").slice(1, 2).join().toLowerCase(),
            ["downcase", ["get", filterProperty]],
        ] as mapboxgl.ExpressionSpecification);
    });

    return filterConditions;
};

// special function that returns a filter expression to restore previous colourings
export function getPaintProperty(
    featureType: string
): mapboxgl.ExpressionSpecification | undefined {
    let voltage: string | undefined = undefined;
    if (featureType === "transmission-lines") {
        voltage = "VOLTAGE";
    } else if (featureType === "bus") {
        voltage = "Voltage";
    }
    return voltage !== undefined
        ? [
              "case",
              // Handle special voltage types first
              ["==", ["get", "TYPE"], "DC"],
              TRANSMISSION_LINE_COLORS["dc"],
              ["==", ["get", "TYPE"], "Others"],
              TRANSMISSION_LINE_COLORS["others"],

              [
                  "match",
                  [
                      "case",
                      // Special exact voltage matches
                      ["==", ["get", voltage], 500],
                      "500kV",
                      ["==", ["get", voltage], 765],
                      "765kV",
                      // For other voltages, determine their range
                      [
                          "all",
                          [">=", ["get", voltage], 0],
                          ["<", ["get", voltage], 100],
                      ],
                      "< 100kV",
                      [
                          "all",
                          [">=", ["get", voltage], 100],
                          ["<", ["get", voltage], 200],
                      ],
                      "100 - 200kV",
                      [
                          "all",
                          [">=", ["get", voltage], 200],
                          ["<", ["get", voltage], 300],
                      ],
                      "200 - 300kV",
                      [
                          "all",
                          [">=", ["get", voltage], 300],
                          ["<", ["get", voltage], 500],
                      ],
                      "300 - 500kV",
                      // Default case for the nested match
                      "Others",
                  ],
                  "< 100kV",
                  TRANSMISSION_LINE_COLORS["<100kv"],
                  "100 - 200kV",
                  TRANSMISSION_LINE_COLORS["100kv-200kv"],
                  "200 - 300kV",
                  TRANSMISSION_LINE_COLORS["200kv-300kv"],
                  "300 - 500kV",
                  TRANSMISSION_LINE_COLORS["300kv-500kv"],
                  "500kV",
                  TRANSMISSION_LINE_COLORS["500kv"],
                  "765kV",
                  TRANSMISSION_LINE_COLORS["765kv"],
                  // Default color if none of the above match
                  TRANSMISSION_LINE_COLORS["others"],
              ],
          ]
        : undefined;
}
